import React, { ComponentProps, useMemo } from 'react';

import { useCartContext } from 'contexts/NewCartContext';

import { useAuth } from 'modules/authentication/presentation/contexts';
import {
  BlackNovemberCountdown,
  NavbarMenuIcon,
  NavigationMenu,
  VialaserLogo,
} from 'shared/presentation/components/atoms';
import {
  PackageSearchBox,
  ProfileHoverCard,
} from 'shared/presentation/components/molecules';
import { CONTACT_DEFAULT_MESSAGES, ICONS } from 'shared/presentation/constants';
import { Link, useLocale, useTheme } from 'shared/presentation/contexts';
import { selectRandomElementFromArray } from 'shared/presentation/helpers';
import {
  useGeolocationCoords,
  useTrackedWhatsappLink,
} from 'shared/presentation/hooks';

import * as S from './styles';

const DesktopNavbar: React.FC = () => {
  const { theme } = useTheme();
  const { t, language } = useLocale('shared');

  const cart = useCartContext();
  const auth = useAuth();
  const geolocation = useGeolocationCoords();

  const customerName = auth.session?.name;

  const message = useMemo(
    () => selectRandomElementFromArray(CONTACT_DEFAULT_MESSAGES[language]),
    [language],
  );

  const trackedLink = useTrackedWhatsappLink({
    event: 'clicou-botao-whats-header',
    message,
  });

  const icons: ComponentProps<typeof NavbarMenuIcon>[] = [
    {
      Icon: ICONS.WHATSAPP,
      type: 'external',
      title: 'Whatsapp',
      to: trackedLink.href,
      onClick: trackedLink.onClickLink,
    },
    {
      Icon: ICONS.CART,
      type: 'internal',
      title: t('Cart'),
      to: '/carrinho',
      badge: cart.count > 0 ? `${cart.count}` : undefined,
    },
    {
      Icon: ICONS.CALENDAR,
      type: 'internal',
      title: t('Schedule session'),
      to: '/agendamento',
    },
  ];

  return (
    <S.Container>
      <S.TopMenu>
        <Link to="/">
          <VialaserLogo
            color={theme.palette.text.main}
            colors={{ via: theme.palette.primary.main }}
            size={190}
          />
        </Link>

        <PackageSearchBox />

        <S.IconMenu>
          {icons.map(icon => (
            <NavbarMenuIcon key={icon.title} {...icon} />
          ))}
        </S.IconMenu>
      </S.TopMenu>

      <BlackNovemberCountdown maxWidth={180} />

      <S.BottomMenu>
        <NavigationMenu
          items={[
            {
              title: t('Buy now'),
              to: '/pacotes',
              content: t('Buy now'),
              type: 'default',
            },
            {
              title: t('Who we are'),
              to: '/quem-somos',
              content: t('Who we are'),
              type: 'default',
            },
            {
              title: t('Our technology'),
              to: '/nossa-tecnologia',
              content: t('Our technology'),
              type: 'default',
            },
            {
              title: t('Blog'),
              to: 'https://vialaser.com.br/blog',
              content: t('Blog'),
              type: 'external',
            },
            {
              title: t('Find a clinic'),
              to: geolocation.coords ? '/unidades-proximas' : '/busca-unidades',
              content: t('Find a clinic'),
              type: 'default',
            },
          ]}
        />

        {customerName ? (
          <ProfileHoverCard customerName={customerName} />
        ) : (
          <NavigationMenu
            items={[
              {
                title: t('Create your account'),
                to: '/registrar',
                content: t('Create your account'),
                type: 'default',
              },
              {
                title: t('Enter'),
                to: '/login',
                content: t('Enter'),
                type: 'default',
              },
            ]}
          />
        )}
      </S.BottomMenu>
    </S.Container>
  );
};

export default DesktopNavbar;
